'use client';

import React, { useState } from 'react';
import { usePoll, Poll as PollType } from './PollContext';

// --- COMPONENTE VISUAL DE UMA ENQUETE (Reutilizável) ---
function PollResultCard({ 
  poll, 
  isHost, 
  onVote, 
  hasVoted, 
  onEnd 
}: { 
  poll: PollType, 
  isHost: boolean, 
  onVote?: (optId: string) => void,
  hasVoted?: boolean,
  onEnd?: () => void
}) {
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);
  const totalVotes = poll.options.reduce((acc, curr) => acc + curr.voters.length, 0);

  const getPercentage = (count: number) => {
    if (totalVotes === 0) return 0;
    return Math.round((count / totalVotes) * 100);
  };

  const toggleDetails = (optId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isHost) return;
    setExpandedOptionId(expandedOptionId === optId ? null : optId);
  };

  return (
    <div className="bg-[#2c2c2c] rounded-lg p-4 border border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-white font-medium text-sm leading-snug pr-4">{poll.question}</h3>
        {poll.isActive ? (
           <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded border border-green-500/30 whitespace-nowrap">Em andamento</span>
        ) : (
           <span className="text-[10px] bg-gray-600/30 text-gray-400 px-2 py-0.5 rounded border border-gray-600 whitespace-nowrap">Encerrada</span>
        )}
      </div>

      <div className="space-y-2">
        {poll.options.map((opt) => {
          const count = opt.voters.length;
          const percent = getPercentage(count);
          const isLeading = Math.max(...poll.options.map(o => o.voters.length)) === count && count > 0;
          const isExpanded = expandedOptionId === opt.id;
          // Mostra resultado se: já votou, é host, ou enquete acabou
          const showResult = hasVoted || isHost || !poll.isActive;

          return (
            <div key={opt.id} className="relative">
              <button
                onClick={() => onVote && onVote(opt.id)}
                disabled={!onVote || hasVoted || !poll.isActive}
                className={`relative w-full text-left p-2.5 rounded-md border transition-all overflow-hidden
                  ${(hasVoted || !poll.isActive)
                    ? 'border-transparent bg-[#1f1f1f] cursor-default' 
                    : 'border-gray-600 hover:border-blue-500 bg-[#333]'
                  }`}
              >
                {/* Barra de Progresso */}
                {showResult && (
                  <div 
                    className={`absolute left-0 top-0 bottom-0 transition-all duration-700 ease-out ${isLeading ? 'bg-blue-600/30' : 'bg-gray-500/20'}`} 
                    style={{ width: `${percent}%` }}
                  />
                )}
                
                <div className="relative flex justify-between items-center z-10">
                  <span className="text-sm text-gray-200 font-medium truncate max-w-[75%]">{opt.text}</span>
                  {showResult && (
                    <div className="flex items-center gap-2">
                       <span className="text-xs font-bold text-white">{percent}%</span>
                       <span className="text-[10px] text-gray-400">({count})</span>
                    </div>
                  )}
                </div>
              </button>

              {/* Detalhes de votantes (Host vê sempre no histórico ou ao clicar) */}
              {isHost && count > 0 && (
                 <button 
                    onClick={(e) => toggleDetails(opt.id, e)}
                    className="w-full text-center text-[10px] text-gray-500 hover:text-gray-300 py-0.5"
                 >
                   {isExpanded ? 'Ocultar' : 'Ver nomes'}
                 </button>
              )}

              {isExpanded && isHost && (
                <div className="bg-[#1a1a1a] p-2 mt-1 text-xs text-gray-300 rounded border border-gray-700/50 animate-in slide-in-from-top-1">
                  <ul className="list-disc list-inside max-h-20 overflow-y-auto custom-scrollbar">
                    {opt.voters.map((v, i) => (
                      <li key={i} className="truncate">{v.name || 'Anônimo'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-600/50">
        <span className="text-xs text-gray-500">Total: {totalVotes} votos</span>
        {poll.isActive && isHost && onEnd && (
          <button 
            onClick={onEnd}
            className="text-xs text-red-400 hover:text-red-300 hover:underline"
          >
            Encerrar Votação
          </button>
        )}
      </div>
    </div>
  );
}

// --- MODAL DE GERENCIAMENTO (CRIAR + HISTÓRICO) ---
export function PollManagementModal() { // Renomeado de PollCreationModal
  const { isPollModalOpen, togglePollModal, createPoll, polls, isHost } = usePoll();
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  
  // State do formulário
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);

  if (!isPollModalOpen) return null;

  // Se não for Host, não deveria ver isso (mas por segurança retornamos null ou só visualização)
  if (!isHost) return null; 

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const addOption = () => setOptions([...options, '']);
  const removeOption = (index: number) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validOptions = options.filter(o => o.trim() !== '');
    if (question && validOptions.length >= 2) {
      createPoll(question, validOptions);
      setQuestion('');
      setOptions(['', '']);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#1e1e1e] rounded-xl w-full max-w-md border border-gray-700 shadow-2xl flex flex-col max-h-[85vh]">
        
        {/* Header com Abas */}
        <div className="flex border-b border-gray-700">
          <button 
            onClick={() => setActiveTab('new')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'new' ? 'text-blue-400 border-b-2 border-blue-400 bg-[#252525]' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Nova Enquete
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'history' ? 'text-blue-400 border-b-2 border-blue-400 bg-[#252525]' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Histórico ({polls.length})
          </button>
        </div>

        {/* Conteúdo Scrollável */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          {activeTab === 'new' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Pergunta</label>
                <input 
                  className="w-full bg-[#2c2c2c] text-white p-3 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  placeholder="Ex: Qual o próximo passo?"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm text-gray-400">Opções</label>
                {options.map((opt, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input 
                      className="w-full bg-[#2c2c2c] text-white p-2 rounded border border-gray-600 focus:border-blue-500 outline-none"
                      value={opt}
                      onChange={e => handleOptionChange(idx, e.target.value)}
                      placeholder={`Opção ${idx + 1}`}
                    />
                    {options.length > 2 && (
                      <button type="button" onClick={() => removeOption(idx)} className="text-red-400 px-2 hover:bg-red-900/20 rounded">✕</button>
                    )}
                  </div>
                ))}
              </div>

              <button type="button" onClick={addOption} className="text-sm text-blue-400 hover:text-blue-300 font-medium">
                + Adicionar opção
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              {polls.length === 0 ? (
                <div className="text-center text-gray-500 py-8">Nenhuma enquete realizada ainda.</div>
              ) : (
                polls.map(poll => (
                  <PollResultCard key={poll.id} poll={poll} isHost={true} />
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3 bg-[#252525] rounded-b-xl">
          <button onClick={togglePollModal} className="px-4 py-2 text-gray-300 hover:text-white text-sm">
            Fechar
          </button>
          {activeTab === 'new' && (
            <button 
              onClick={handleSubmit}
              disabled={!question || options.filter(o => o.trim()).length < 2} 
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium shadow-lg"
            >
              Iniciar Enquete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- WIDGET FLUTUANTE DA ENQUETE ATIVA ---
export function PollWidget() {
  const { activePoll, vote, hasVotedInActive, isHost, endPoll } = usePoll();
  const [isMinimized, setIsMinimized] = useState(false);

  // REGRA DE OURO: Se não tem enquete ATIVA, não mostra nada na tela do usuário.
  if (!activePoll) return null;

  return (
    <div className={`fixed bottom-24 right-4 z-[40] w-80 bg-[#1e1e1e] border border-gray-700 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ${isMinimized ? 'w-auto' : 'w-80'}`}>
       
       <div className="flex items-center justify-between p-3 bg-[#252525] border-b border-gray-700 cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
        <div className="flex items-center gap-2">
          <span className="text-xl">📊</span>
          {!isMinimized && (
            <div className="flex flex-col">
              <span className="font-semibold text-white text-sm">Enquete Ativa</span>
            </div>
          )}
        </div>
        {!isMinimized && <button className="text-gray-400 hover:text-white">▼</button>}
      </div>

      {!isMinimized && (
        <div className="p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
          <PollResultCard 
            poll={activePoll} 
            isHost={isHost} 
            hasVoted={hasVotedInActive} 
            onVote={(optId) => vote(activePoll.id, optId)}
            onEnd={() => endPoll(activePoll.id)}
          />
        </div>
      )}
    </div>
  );
}